import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 } from "uuid";
import { demoAcceptedAtMs } from "../components/Annie/annieDemoReplacement";
import { appendWorkStep } from "../components/Annie/annieWorkSteps";
import { usePerformGlobalRefresh } from "../components/header/hooks";
import { useAnalytics } from "../contexts/analytics/hooks";
import { useChosenDepartment } from "../contexts/ChosenDepartment";
import { useDraftMode } from "../contexts/DraftModeContext";
import { localizeAnnieDetailParams } from "../utils/annieDetailParams";
import {
	ANNIE_GUIDE_UNAVAILABLE_CODE,
	type AnnieCard,
	type AnnieClarificationCard,
	type AnnieClarificationOption,
	type AnnieFeedbackState,
	type AnnieNurseChoiceGroup,
	type AnnieNurseChoiceOption,
	type AnnieProposalCard,
	type AnnieReadChoice,
	type AnnieSource,
	AnnieStatusKind,
	type AnnieSuggestion,
	type AnnieVideo,
	type AnnieWalkthrough,
	parseOptions,
	parseTimeRangeDefaults,
	streamAnnieAssist,
} from "../utils/annieStream";
import axiosInstance from "../utils/networkWrapper";
import { extractAnnieError } from "./annieErrors";
import { useAnnieFeedback } from "./useAnnieFeedback";
import { useIsMobile } from "./useMobile";

export type AnnieProposalUiStatus = "pending" | "approving" | "done" | "stale" | "expired" | "failed" | "rejected";

/** A demo department's simulated replacement search, returned by /annie/confirm for a call-off. */
export interface AnnieDemoReplacement {
	declined: string[];
	accepted: string;
	eligible_count: number;
}

/** A demo replacement on a confirmed card, with when its replies started arriving. */
export interface AnnieDemoReplacementReveal {
	replacement: AnnieDemoReplacement;
	startedAtMs: number;
}

/** POST /annie/confirm. `demo_replacement` is present only for a demo department's call-off. */
interface AnnieConfirmResponse {
	status: string;
	result: { demo_replacement?: AnnieDemoReplacement };
}

/** How much answer the administrator asked for — the scale, lowest → highest, mirrors the backend enum. */
export type AnnieResponseDepth = "short" | "standard" | "detailed";

export type AnnieChatItem =
	| { id: string; kind: "user"; text: string }
	| {
			id: string;
			kind: "assistant";
			text: string;
			sources?: AnnieSource[];
			suggestions?: AnnieSuggestion[];
			nurseChoices?: AnnieNurseChoiceGroup[];
			/** Live-only context for continuing this read after choosing a nurse. */
			readChoice?: AnnieReadChoice;
			walkthrough?: AnnieWalkthrough;
			videos?: AnnieVideo[];
			/** The status lines Annie streamed while working on this answer; absent when she streamed none. */
			workSteps?: string[];
			conversationId?: number;
			feedback?: AnnieFeedbackState;
			isSavingFeedback?: boolean;
	  }
	| { id: string; kind: "error"; text: string }
	| { id: string; kind: "proposal"; card: AnnieProposalCard; status: AnnieProposalUiStatus; statusNote?: string; demoReplacement?: AnnieDemoReplacementReveal }
	| { id: string; kind: "clarification"; card: AnnieClarificationCard; isChoosing: boolean; chosenOptionId?: string; isExpired: boolean };

/** /annie/replan returns the bare card fields (no `kind`); the axios interceptor turns ISO dates into Luxon. */
type AnnieReplanResponse = (Omit<AnnieProposalCard, "kind" | "expires_at"> & { expires_at: string | DateTime }) | Omit<AnnieClarificationCard, "kind">;

/** One row in the session picker (GET /annie/sessions); `title` = the session's first user message. */
export interface AnnieSessionSummary {
	session_id: string;
	title: string;
	/** ISO on the wire; the axios interceptor converts it to Luxon. */
	last_activity: DateTime;
}

interface AnnieTranscriptCard {
	kind: "proposal" | "clarification";
	id: string;
	text: string;
}

/** One past turn (GET /annie/sessions/{id}). */
interface AnnieTranscriptMessage {
	role: "user" | "assistant";
	text: string;
	/** Exact visible answer for card turns; null for legacy and non-card rows. */
	display_text: string | null;
	/** Cards emitted in this turn, including ones that are no longer pending. */
	transcript_cards: AnnieTranscriptCard[] | null;
	created_at: DateTime;
	conversation_id: number;
	feedback: AnnieFeedbackState | null;
}

const hasCardHistory = (
	message: AnnieTranscriptMessage,
): message is AnnieTranscriptMessage & { display_text: string; transcript_cards: AnnieTranscriptCard[] } =>
	message.display_text !== null && message.display_text !== undefined && message.transcript_cards !== null && message.transcript_cards !== undefined;

/** GET /annie/sessions/{id}: past turns plus any still-pending cards (bare, same wire shape as /replan). */
interface AnnieTranscriptResponse {
	messages: AnnieTranscriptMessage[];
	pending_clarifications: Omit<AnnieClarificationCard, "kind">[];
	pending_proposals: (Omit<AnnieProposalCard, "kind" | "expires_at"> & { expires_at: string | DateTime })[];
}

const toCard = (data: AnnieReplanResponse): AnnieCard => {
	if ("proposal_id" in data) {
		const expiresAtIso = typeof data.expires_at === "string" ? data.expires_at : data.expires_at.toISO();
		if (expiresAtIso === null) throw new Error("annie replan returned an invalid expires_at");
		return { ...data, kind: "proposal", expires_at: expiresAtIso };
	}
	// /replan and transcript payloads skip the SSE parser, so normalize here too: run options through
	// parseOptions so the approve nurse-picker's per-row pickup metrics are nested (option.pickup) and the
	// picker renders as the cost/OT table (not plain chips); `kind` is forced to "clarification" (the FE
	// union discriminator) — the time-picker signal is `slot === "times"`.
	return { ...data, kind: "clarification", options: parseOptions(data.options), defaults: parseTimeRangeDefaults(data.defaults) };
};

/** The in-flight turn: its tool steps, the tool step she's on, and the line she's on now (a tool step or her lead-in). */
interface TurnProgress {
	steps: readonly string[];
	activeStep: string | null;
	current: string | null;
}

// One shared empty trail so clearing an already-empty trail (every token frame) is a state no-op.
const NO_PROGRESS: TurnProgress = { steps: [], activeStep: null, current: null };

/** Why an in-flight answer was abandoned, reported on `annie_assist_aborted`. Closing the panel is not one: the answer keeps streaming. */
export enum AnnieAbortReason {
	Stop = "stop",
	Unmount = "unmount",
	DepartmentSwitch = "department_switch",
	NewSession = "new_session",
	SessionSwitch = "session_switch",
}

const ANNIE_ABORT_REASONS: ReadonlySet<unknown> = new Set(Object.values(AnnieAbortReason));
const isAnnieAbortReason = (value: unknown): value is AnnieAbortReason => ANNIE_ABORT_REASONS.has(value);

/** A tool dispatch is a step (a repeat re-activates its row); the model's lead-in only changes the current line. */
const advanceProgress = (progress: TurnProgress, text: string, kind: AnnieStatusKind): TurnProgress => {
	const line = text.trim();
	if (line === "") return progress;
	if (kind === AnnieStatusKind.Preamble) return line === progress.current ? progress : { ...progress, current: line };
	const steps = appendWorkStep(progress.steps, line);
	return steps.length === progress.steps.length && line === progress.current && line === progress.activeStep
		? progress
		: { steps, activeStep: line, current: line };
};

const toCardItem = (card: AnnieCard): AnnieChatItem =>
	card.kind === "proposal"
		? { id: v4(), kind: "proposal", card, status: "pending" }
		: { id: v4(), kind: "clarification", card, isChoosing: false, isExpired: false };

/**
 * Per-conversation state for the Annie scheduler assistant: the active session
 * (fresh uuid per new chat, reset on department switch), the ordered
 * message/card list, the department's recent-session picker, and the backend
 * interactions (assist stream / replan / confirm / session list-load-delete).
 */
export const useAnnie = ({ sessionsEnabled }: { sessionsEnabled: boolean }) => {
	const [items, setItems] = useState<AnnieChatItem[]>([]);
	const [progress, setProgress] = useState<TurnProgress>(NO_PROGRESS);
	const statusText = progress.current;
	const workSteps = progress.steps;
	const activeWorkStep = progress.activeStep;
	const [isStreaming, setIsStreaming] = useState(false);
	const [sessionId, setSessionId] = useState<string>(() => v4());
	const [isLoadingSession, setIsLoadingSession] = useState(false);
	const [deletingSessionId, setDeletingSessionId] = useState<string | null>(null);
	const abortRef = useRef<AbortController | null>(null);
	// Monotonic ticket so an out-of-date transcript load (fast session switching) never applies.
	const loadTicketRef = useRef(0);

	const { chosenDepartment } = useChosenDepartment();
	const departmentId = chosenDepartment.department_id;
	const { isDraftMode } = useDraftMode();
	// The backend decides from this whether a walkthrough is offerable at all.
	const { isMobile } = useIsMobile();
	const { track, trackError } = useAnalytics();
	const { performGlobalRefresh } = usePerformGlobalRefresh({});
	const { t, i18n } = useTranslation();

	const translateErrorKey = useCallback((key?: string): string => (key && i18n.exists(key) ? t(key) : t("annie.error.generic")), [t, i18n]);

	const appendItem = useCallback((item: AnnieChatItem) => setItems((prev) => [...prev, item]), []);

	// A demo call-off refreshes the schedule when its replacement "accepts". Held here, not in the card,
	// so closing the panel mid-search can't drop it; a department switch cancels it.
	const demoRefreshTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
	// biome-ignore lint/correctness/useExhaustiveDependencies: departmentId is the trigger, not a body dependency
	useEffect(() => {
		const timers = demoRefreshTimersRef;
		return () => {
			for (const timer of timers.current) clearTimeout(timer);
			timers.current = [];
		};
	}, [departmentId]);

	const updateProposalItem = useCallback(
		(itemId: string, patch: Partial<Pick<Extract<AnnieChatItem, { kind: "proposal" }>, "status" | "statusNote" | "demoReplacement">>) =>
			setItems((prev) => prev.map((item) => (item.id === itemId && item.kind === "proposal" ? { ...item, ...patch } : item))),
		[],
	);

	const queryClient = useQueryClient();
	const sessionsQueryKey = useMemo(() => ["annie-sessions", departmentId], [departmentId]);
	const {
		data: sessions,
		isLoading: isLoadingSessions,
		isError: isSessionsError,
		refetch: refetchSessions,
	} = useQuery({
		queryKey: sessionsQueryKey,
		queryFn: async () => {
			const response = await axiosInstance.get<{ sessions: AnnieSessionSummary[] }>("/annie/sessions", { params: { department_id: departmentId } });
			return response.data.sessions;
		},
		// Only fetched while the panel is open — no background traffic for every head nurse.
		enabled: sessionsEnabled && !!departmentId,
	});

	const cancel = useCallback(() => {
		abortRef.current?.abort(AnnieAbortReason.Stop);
		abortRef.current = null;
		setIsStreaming(false);
		setProgress(NO_PROGRESS);
	}, []);

	// Abort an in-flight stream on unmount.
	useEffect(() => () => abortRef.current?.abort(AnnieAbortReason.Unmount), []);

	// Annie sessions are department-scoped on the backend — switching departments starts a fresh conversation.
	// biome-ignore lint/correctness/useExhaustiveDependencies: departmentId is the trigger, not a body dependency
	useEffect(() => {
		loadTicketRef.current += 1;
		abortRef.current?.abort(AnnieAbortReason.DepartmentSwitch);
		abortRef.current = null;
		setSessionId(v4());
		setItems([]);
		setIsStreaming(false);
		setProgress(NO_PROGRESS);
		setIsLoadingSession(false);
	}, [departmentId]);

	/** Start a fresh conversation (new uuid, empty transcript); aborts anything in flight. */
	const newSession = useCallback(() => {
		loadTicketRef.current += 1;
		abortRef.current?.abort(AnnieAbortReason.NewSession);
		abortRef.current = null;
		setSessionId(v4());
		setItems([]);
		setIsStreaming(false);
		setProgress(NO_PROGRESS);
		setIsLoadingSession(false);
		track("annie_session_new", { department_id: departmentId });
	}, [departmentId, track]);

	/** Load a past session's transcript (plus any still-pending card) and make it the active session — sending then continues it. */
	const switchSession = useCallback(
		async (targetSessionId: string) => {
			if (targetSessionId === sessionId) return;
			const ticket = ++loadTicketRef.current;
			abortRef.current?.abort(AnnieAbortReason.SessionSwitch);
			abortRef.current = null;
			setSessionId(targetSessionId);
			setItems([]);
			setIsStreaming(false);
			setProgress(NO_PROGRESS);
			setIsLoadingSession(true);
			track("annie_session_switch", { department_id: departmentId, session_id: targetSessionId });
			try {
				const response = await axiosInstance.get<AnnieTranscriptResponse>(`/annie/sessions/${targetSessionId}`, {
					params: { department_id: departmentId },
				});
				if (loadTicketRef.current !== ticket) return;
				const { messages, pending_clarifications, pending_proposals } = response.data;
				const pendingCardIds = new Set([
					...pending_proposals.map((card) => `proposal:${card.proposal_id}`),
					...pending_clarifications.map((card) => `clarification:${card.clarification_id}`),
				]);
				const loaded: AnnieChatItem[] = messages.flatMap((message): AnnieChatItem[] => {
					// DEFAULT: old backend responses and legacy rows use the stored text until both
					// structured fields can separate the visible answer from card history.
					let visibleText = message.text;
					let transcriptCards: AnnieTranscriptCard[] | null = null;
					if (hasCardHistory(message)) {
						visibleText = message.display_text;
						transcriptCards = message.transcript_cards;
					}
					const turnItems: AnnieChatItem[] = [];
					// Some rows are persisted as an "annie.*" i18n key, not literal text: reason-picker chip
					// labels (user rows) and terminal-decision notes like annie.transcript.dismissed (assistant
					// rows). The prefix guard keeps genuine prose that matches a plain-word key (e.g. "AM") intact.
					const text = visibleText.startsWith("annie.") && i18n.exists(visibleText) ? t(visibleText) : visibleText;
					if (message.role === "assistant") {
						if (text !== "")
							turnItems.push({
								id: v4(),
								kind: "assistant",
								text,
								...(message.feedback === null ? {} : { conversationId: message.conversation_id, feedback: message.feedback }),
							});
						if (transcriptCards !== null) {
							for (const card of transcriptCards) {
								if (!pendingCardIds.has(`${card.kind}:${card.id}`)) turnItems.push({ id: v4(), kind: "assistant", text: card.text });
							}
						}
						return turnItems;
					}
					return [{ id: v4(), kind: "user", text }];
				});
				// Proposal first, clarification last — the most actionable card sits at the bottom.
				const pendingCards = [...pending_proposals.map(toCard), ...pending_clarifications.map(toCard)];
				const lastMessage = messages[messages.length - 1];
				const last = loaded[loaded.length - 1];
				// A row saved before card history was stored separately is "<prose> <card text>"; the pending
				// card is the sole rendering of its own text, so trim that suffix from the bubble.
				if (lastMessage?.role === "assistant" && !hasCardHistory(lastMessage) && last?.kind === "assistant") {
					const matched = pendingCards.find((card) => last.text.endsWith(card.kind === "proposal" ? card.summary : card.question));
					if (matched) {
						const cardText = matched.kind === "proposal" ? matched.summary : matched.question;
						const remainder = last.text.slice(0, last.text.length - cardText.length).trimEnd();
						if (remainder) last.text = remainder;
						else loaded.pop();
					}
				}
				setItems([...loaded, ...pendingCards.map(toCardItem)]);
			} catch (error) {
				if (loadTicketRef.current !== ticket) return;
				const { status, key } = extractAnnieError(error);
				appendItem({ id: v4(), kind: "error", text: translateErrorKey(key) });
				trackError("annie_session_load_error", { department_id: departmentId, session_id: targetSessionId, status, message_key: key });
			} finally {
				if (loadTicketRef.current === ticket) setIsLoadingSession(false);
			}
		},
		[sessionId, departmentId, appendItem, track, trackError, translateErrorKey, t, i18n],
	);

	/** Delete a past session; deleting the active one starts a fresh chat. */
	const deleteSession = useCallback(
		async (targetSessionId: string) => {
			if (isDraftMode) {
				appendItem({ id: v4(), kind: "error", text: t("annie.draft.disabled") });
				return;
			}
			if (deletingSessionId !== null) return;
			setDeletingSessionId(targetSessionId);
			track("annie_session_delete_start", { department_id: departmentId, session_id: targetSessionId });
			try {
				await axiosInstance.delete(`/annie/sessions/${targetSessionId}`, { params: { department_id: departmentId } });
				track("annie_session_delete_success", { department_id: departmentId, session_id: targetSessionId });
				if (targetSessionId === sessionId) newSession();
			} catch (error) {
				const { status, key } = extractAnnieError(error);
				trackError("annie_session_delete_error", { department_id: departmentId, session_id: targetSessionId, status, message_key: key });
				// 404 = already gone server-side; the refetch below drops it from the list.
				if (status !== 404) appendItem({ id: v4(), kind: "error", text: translateErrorKey(key) });
				else if (targetSessionId === sessionId) newSession();
			} finally {
				setDeletingSessionId(null);
				void refetchSessions();
			}
		},
		[isDraftMode, deletingSessionId, sessionId, departmentId, appendItem, newSession, refetchSessions, t, track, trackError, translateErrorKey],
	);

	const send = useCallback(
		async (
			rawMessage: string,
			responseDepth: AnnieResponseDepth,
			opts?: { regenerateConversationId?: number; skipUserBubble?: boolean; guideTopicId?: string; readChoice?: AnnieReadChoice },
		) => {
			const message = rawMessage.trim();
			if (!message || isStreaming || isLoadingSession) return false;
			if (isDraftMode) {
				appendItem({ id: v4(), kind: "error", text: t("annie.draft.disabled") });
				return false;
			}
			const startedAt = performance.now();
			let succeeded = false;
			const controller = new AbortController();
			abortRef.current = controller;
			// On regenerate the question bubble is already on screen (only the answer is being replaced).
			const userItemId = opts?.skipUserBubble === true ? null : v4();
			if (userItemId !== null) appendItem({ id: userItemId, kind: "user", text: message });
			setIsStreaming(true);
			setProgress(NO_PROGRESS);
			track("annie_assist_start", { department_id: departmentId, session_id: sessionId, response_depth: responseDepth });

			let assistantItemId: string | null = null;
			let streamErrored = false;
			let firstStatusMs: number | null = null;
			let firstAnswerMs: number | null = null;
			let doneMs: number | null = null;
			// Per-send mirror of progress: back-to-back frames build on the latest trail, not a stale render's.
			let turn = NO_PROGRESS;
			const clearTurnSteps = () => {
				turn = NO_PROGRESS;
				setProgress(NO_PROGRESS);
			};
			const removePartialAnswer = () => {
				if (assistantItemId === null) return;
				const targetId = assistantItemId;
				assistantItemId = null;
				setItems((prev) => prev.filter((item) => item.id !== targetId));
			};
			try {
				await streamAnnieAssist(
					{
						message,
						session_id: sessionId,
						department_id: departmentId,
						response_depth: responseDepth,
						...(opts?.guideTopicId === undefined ? {} : { guide_topic_id: opts.guideTopicId }),
						...(opts?.readChoice === undefined ? {} : { read_choice: opts.readChoice }),
						regenerate_conversation_id: opts?.regenerateConversationId,
						surface: isMobile ? "mobile" : "desktop",
					},
					(frame) => {
						if (streamErrored) return;
						switch (frame.event) {
							case "status": {
								const next = advanceProgress(turn, frame.text, frame.kind);
								if (next === turn) break;
								if (firstStatusMs === null) firstStatusMs = performance.now() - startedAt;
								turn = next;
								setProgress(next);
								break;
							}
							case "token": {
								if (firstAnswerMs === null && frame.text.trim() !== "") firstAnswerMs = performance.now() - startedAt;
								if (assistantItemId === null) {
									assistantItemId = v4();
									appendItem({
										id: assistantItemId,
										kind: "assistant",
										text: frame.text,
										...(opts?.readChoice === undefined ? {} : { readChoice: opts.readChoice }),
										...(turn.steps.length > 0 ? { workSteps: [...turn.steps] } : {}),
									});
								} else {
									const targetId = assistantItemId;
									setItems((prev) =>
										prev.map((item) => (item.id === targetId && item.kind === "assistant" ? { ...item, text: item.text + frame.text } : item)),
									);
								}
								clearTurnSteps();
								break;
							}
							case "card":
								clearTurnSteps();
								appendItem(toCardItem(frame.card));
								break;
							case "sources": {
								// Optional citation chrome: attach to the answer once it exists. A source frame with no
								// answer to hang on is a no-op, not a turn-failing error.
								if (assistantItemId === null) break;
								const sourcesTargetId = assistantItemId;
								setItems((prev) => prev.map((item) => (item.id === sourcesTargetId && item.kind === "assistant" ? { ...item, sources: frame.sources } : item)));
								break;
							}
							case "suggestions": {
								if (assistantItemId === null) break;
								const suggestionsTargetId = assistantItemId;
								setItems((prev) =>
									prev.map((item) => (item.id === suggestionsTargetId && item.kind === "assistant" ? { ...item, suggestions: frame.suggestions } : item)),
								);
								break;
							}
							case "nurse_choices": {
								if (assistantItemId === null) {
									assistantItemId = v4();
									appendItem({
										id: assistantItemId,
										kind: "assistant",
										// A mixed card/read turn can offer choices without answer prose.
										text: "",
										...(turn.steps.length > 0 ? { workSteps: [...turn.steps] } : {}),
									});
								}
								clearTurnSteps();
								const targetId = assistantItemId;
								setItems((prev) =>
									prev.map((item) =>
										item.id === targetId && item.kind === "assistant"
											? {
													...item,
													nurseChoices: frame.groups,
													// DEFAULT: the original request has no selected identities until a chip is tapped.
													readChoice: opts?.readChoice === undefined ? { question: message, selections: [] } : opts.readChoice,
												}
											: item,
									),
								);
								break;
							}
							case "walkthrough": {
								// Same rule as sources: an offer with no answer to hang on is a no-op, not a failure.
								if (assistantItemId === null) break;
								const walkthroughTargetId = assistantItemId;
								setItems((prev) =>
									prev.map((item) => (item.id === walkthroughTargetId && item.kind === "assistant" ? { ...item, walkthrough: frame.walkthrough } : item)),
								);
								break;
							}
							case "videos": {
								// Same rule as sources: chips with no answer to hang on are a no-op, not a failure.
								if (assistantItemId === null) break;
								const videosTargetId = assistantItemId;
								setItems((prev) => prev.map((item) => (item.id === videosTargetId && item.kind === "assistant" ? { ...item, videos: frame.videos } : item)));
								break;
							}
							case "feedback_target": {
								if (assistantItemId === null) throw new Error("annie feedback target arrived before answer tokens");
								const targetId = assistantItemId;
								setItems((prev) =>
									prev.map((item) =>
										item.id === targetId && item.kind === "assistant" ? { ...item, conversationId: frame.conversation_id, feedback: frame.feedback } : item,
									),
								);
								break;
							}
							case "error":
								streamErrored = true;
								clearTurnSteps();
								removePartialAnswer();
								appendItem({ id: v4(), kind: "error", text: frame.code === ANNIE_GUIDE_UNAVAILABLE_CODE ? t("annie.guide.unavailable") : frame.text });
								trackError("annie_assist_error", { department_id: departmentId, error_code: frame.code, elapsed_ms: performance.now() - startedAt });
								break;
							case "done":
								doneMs = performance.now() - startedAt;
								clearTurnSteps();
								break;
						}
					},
					controller.signal,
				);
				if (!streamErrored) {
					if (doneMs === null) throw new Error("annie assist stream closed before done frame");
					succeeded = true;
					track("annie_assist_success", {
						department_id: departmentId,
						session_id: sessionId,
						response_depth: responseDepth,
						elapsed_ms: performance.now() - startedAt,
						...(firstStatusMs === null ? {} : { first_status_ms: firstStatusMs }),
						...(firstAnswerMs === null ? {} : { first_answer_ms: firstAnswerMs }),
						done_ms: doneMs,
					});
				}
			} catch (error) {
				// A deliberate abort (Stop, a new or switched chat, a department switch, unmount) is not an error.
				if (controller.signal.aborted) {
					const reason: unknown = controller.signal.reason;
					if (!isAnnieAbortReason(reason)) throw new Error(`annie stream aborted without a known reason: ${String(reason)}`, { cause: error });
					track("annie_assist_aborted", {
						department_id: departmentId,
						session_id: sessionId,
						reason,
						elapsed_ms: performance.now() - startedAt,
						had_partial_answer: assistantItemId !== null,
					});
					if (doneMs === null) removePartialAnswer();
					return false;
				}
				removePartialAnswer();
				const { status, key } = extractAnnieError(error);
				appendItem({ id: v4(), kind: "error", text: translateErrorKey(key) });
				trackError("annie_assist_error", {
					department_id: departmentId,
					status,
					message_key: key,
					error_message: error instanceof Error ? error.message : String(error),
					elapsed_ms: performance.now() - startedAt,
				});
			} finally {
				// A failed selection must leave its source choices live; remove only this turn's optimistic label.
				if (!succeeded && opts?.readChoice !== undefined && userItemId !== null) {
					setItems((prev) => prev.filter((item) => item.id !== userItemId));
				}
				if (abortRef.current === controller) {
					abortRef.current = null;
					setIsStreaming(false);
					setProgress(NO_PROGRESS);
					// The user turn was persisted server-side before the stream — the picker's title/order may have changed.
					// Invalidate, not refetch: a turn that finished behind a closed panel waits for the list to be shown.
					void queryClient.invalidateQueries({ queryKey: sessionsQueryKey });
				}
			}
			return succeeded;
		},
		[
			isStreaming,
			isLoadingSession,
			isDraftMode,
			departmentId,
			sessionId,
			isMobile,
			appendItem,
			queryClient,
			sessionsQueryKey,
			t,
			track,
			trackError,
			translateErrorKey,
		],
	);

	const chooseReadNurse = useCallback(
		async (itemId: string, name: string, option: AnnieNurseChoiceOption, responseDepth: AnnieResponseDepth) => {
			if (abortRef.current !== null || isStreaming || isLoadingSession) return;
			const answerIndex = items.findIndex((item) => item.id === itemId);
			const answer = items[answerIndex];
			if (answer?.kind !== "assistant" || items.slice(answerIndex + 1).some((item) => item.kind === "user")) return;
			const group = answer.nurseChoices?.find((choice) => choice.name === name);
			if (answer.readChoice === undefined || group === undefined || !group.options.some((choice) => choice.nurse_id === option.nurse_id)) {
				throw new Error("annie selected nurse is missing from the live read choices");
			}
			track("annie_nurse_choice_selected", { department_id: departmentId });
			await send(option.label, responseDepth, {
				readChoice: {
					question: answer.readChoice.question,
					selections: [...answer.readChoice.selections.filter((selection) => selection.name !== name), { name, nurse_id: option.nurse_id }],
				},
			});
		},
		[items, isStreaming, isLoadingSession, departmentId, send, track],
	);

	// Re-run the question that produced `answerItemId`, replacing that answer in place. The backend drops
	// the stale answer + its question (by conversationId) so the model never sees its previous attempt.
	const regenerate = useCallback(
		async (answerItemId: string, conversationId: number, responseDepth: AnnieResponseDepth) => {
			if (isStreaming || isLoadingSession) return;
			const answerIndex = items.findIndex((item) => item.id === answerItemId);
			if (answerIndex < 1) return;
			const answer = items[answerIndex];
			if (answer.kind !== "assistant") return;
			const question = items
				.slice(0, answerIndex)
				.reverse()
				.find((item) => item.kind === "user");
			if (question === undefined || question.kind !== "user") return;
			track("annie_answer_regenerate", { department_id: departmentId });
			// Snapshot before optimistically dropping the old answer, so a failed regenerate restores it
			// exactly (the backend keeps the original until the fresh answer persists, so nothing is lost).
			const snapshot = items;
			// New chat, another chat and a department switch all move this ticket on and replace the transcript.
			const conversationTicket = loadTicketRef.current;
			setItems((prev) => prev.slice(0, answerIndex));
			const ok = await send(question.text, responseDepth, {
				regenerateConversationId: conversationId,
				skipUserBubble: true,
				...(answer.readChoice !== undefined && answer.readChoice.selections.length > 0 ? { readChoice: answer.readChoice } : {}),
			});
			if (!ok && loadTicketRef.current === conversationTicket) {
				// Restoring the snapshot drops send()'s inline error item, so the failure is silent in the UI —
				// keep it observable in analytics (covers pre-stream 409s the generic annie_assist_* events miss).
				setItems(snapshot);
				trackError("annie_answer_regenerate_failed", { department_id: departmentId });
			}
		},
		[items, isStreaming, isLoadingSession, departmentId, send, track, trackError],
	);

	const saveFeedback = useAnnieFeedback(items, setItems);

	// Shared /replan failure policy for the picker (choose + submitTimes): Gone (404/410) or needs_input
	// (422) are TERMINAL — the CARD itself is unusable, so retire it (disable + dim); transient errors
	// (network/5xx) keep the chips clickable to retry. `clearChoice` drops the pending selection (choose
	// only — submitTimes has no chosen chip to clear).
	//
	// 409 is deliberately NOT terminal: only the option just picked is unusable (e.g. that nurse has no
	// shift on the date), so the picker stays live rather than making one mis-tap force a full retype.
	const handleReplanError = useCallback(
		(itemId: string, card: AnnieClarificationCard, error: unknown, clearChoice: boolean) => {
			const { status, key, detailKey, detailParams } = extractAnnieError(error);
			// Keyed on the message, not the bare status: 409 means "only that option is dead, pick another"
			// for option.unavailable, but "this is over" for already_answered (and any future 409). Mirrors
			// the /confirm handler below, and makes an unrecognized 409 fail CLOSED rather than open.
			const isRetryableConflict = status === 409 && key === "annie.replan.option.unavailable";
			const isTerminal = !isRetryableConflict && (status === 404 || status === 409 || status === 410 || status === 422);
			setItems((prev) =>
				prev.map((item) =>
					item.id === itemId && item.kind === "clarification"
						? { ...item, isChoosing: false, isExpired: item.isExpired || isTerminal, ...(clearChoice ? { chosenOptionId: undefined } : {}) }
						: item,
				),
			);
			// The backend's specific reason wins over the generic key — both are translated.
			const text = detailKey !== undefined && i18n.exists(detailKey) ? t(detailKey, localizeAnnieDetailParams(detailParams)) : translateErrorKey(key);
			appendItem({ id: v4(), kind: "error", text });
			trackError("annie_replan_error", { department_id: departmentId, clarification_id: card.clarification_id, status, message_key: key });
		},
		[departmentId, appendItem, translateErrorKey, trackError, t, i18n],
	);

	const choose = useCallback(
		async (itemId: string, card: AnnieClarificationCard, option: AnnieClarificationOption) => {
			if (isDraftMode) {
				appendItem({ id: v4(), kind: "error", text: t("annie.draft.disabled") });
				return;
			}
			setItems((prev) =>
				prev.map((item) => (item.id === itemId && item.kind === "clarification" ? { ...item, isChoosing: true, chosenOptionId: option.id } : item)),
			);
			track("annie_replan_start", { department_id: departmentId, clarification_id: card.clarification_id, slot: card.slot });
			try {
				const response = await axiosInstance.post<AnnieReplanResponse>("/annie/replan", {
					clarification_id: card.clarification_id,
					slot: card.slot,
					chosen_id: option.id,
				});
				const nextItem = toCardItem(toCard(response.data));
				setItems((prev) => prev.map((item) => (item.id === itemId ? nextItem : item)));
				track("annie_replan_success", { department_id: departmentId, clarification_id: card.clarification_id, slot: card.slot });
			} catch (error) {
				handleReplanError(itemId, card, error, true);
			}
		},
		[isDraftMode, departmentId, appendItem, t, track, handleReplanError],
	);

	const submitTimes = useCallback(
		async (itemId: string, card: AnnieClarificationCard, values: { start_time: string; end_time: string }) => {
			if (isDraftMode) {
				appendItem({ id: v4(), kind: "error", text: t("annie.draft.disabled") });
				return;
			}
			setItems((prev) => prev.map((item) => (item.id === itemId && item.kind === "clarification" ? { ...item, isChoosing: true } : item)));
			track("annie_replan_start", { department_id: departmentId, clarification_id: card.clarification_id, slot: card.slot });
			try {
				const response = await axiosInstance.post<AnnieReplanResponse>("/annie/replan", {
					clarification_id: card.clarification_id,
					slot: card.slot,
					values,
				});
				const nextItem = toCardItem(toCard(response.data));
				setItems((prev) => prev.map((item) => (item.id === itemId ? nextItem : item)));
				track("annie_replan_success", { department_id: departmentId, clarification_id: card.clarification_id, slot: card.slot });
			} catch (error) {
				handleReplanError(itemId, card, error, false);
			}
		},
		[isDraftMode, departmentId, appendItem, t, track, handleReplanError],
	);

	const approve = useCallback(
		async (itemId: string, card: AnnieProposalCard) => {
			if (isDraftMode) {
				appendItem({ id: v4(), kind: "error", text: t("annie.draft.disabled") });
				return;
			}
			updateProposalItem(itemId, { status: "approving" });
			track("annie_confirm_start", { department_id: departmentId, proposal_id: card.proposal_id, verb: card.verb });
			try {
				const response = await axiosInstance.post<AnnieConfirmResponse>("/annie/confirm", { proposal_id: card.proposal_id });
				const replacement = response.data.result.demo_replacement;
				updateProposalItem(itemId, { status: "done", demoReplacement: replacement && { replacement, startedAtMs: Date.now() } });
				track("annie_confirm_success", { department_id: departmentId, proposal_id: card.proposal_id, verb: card.verb });
				// The staffing grid queries are enabled:false — the app-wide refresh is the only sanctioned refetch path.
				if (replacement === undefined) await performGlobalRefresh();
				else demoRefreshTimersRef.current.push(setTimeout(() => void performGlobalRefresh(), demoAcceptedAtMs(replacement)));
			} catch (error) {
				const { status, key } = extractAnnieError(error);
				trackError("annie_confirm_error", { department_id: departmentId, proposal_id: card.proposal_id, status, message_key: key });
				if (status === 409 && key === "annie.confirm.stale") {
					// The proposal was reset to pending on the backend; the user must re-ask against fresh state.
					updateProposalItem(itemId, { status: "stale", statusNote: t("annie.confirm.stale") });
				} else if (status === 410) {
					updateProposalItem(itemId, { status: "expired", statusNote: translateErrorKey(key) });
				} else if (status === 403 || status === 409 || status === 501 || status === 503) {
					// Retrying won't change the answer (disabled / already confirmed / unsupported / kill switch).
					updateProposalItem(itemId, { status: "failed", statusNote: translateErrorKey(key) });
				} else {
					// Transient (network / 5xx): keep the card approvable and surface the error inline.
					updateProposalItem(itemId, { status: "pending" });
					appendItem({ id: v4(), kind: "error", text: translateErrorKey(key) });
				}
			}
		},
		[isDraftMode, departmentId, appendItem, updateProposalItem, t, track, trackError, translateErrorKey, performGlobalRefresh],
	);

	// Optimistic dismissal; the backend records status=rejected so the model's next turn knows the
	// proposal was declined (its CAS never clobbers an in-flight confirm).
	const reject = useCallback(
		async (itemId: string, card: AnnieProposalCard) => {
			if (isDraftMode) {
				appendItem({ id: v4(), kind: "error", text: t("annie.draft.disabled") });
				return;
			}
			updateProposalItem(itemId, { status: "rejected" });
			track("annie_proposal_rejected", { department_id: departmentId, proposal_id: card.proposal_id, verb: card.verb });
			try {
				await axiosInstance.post("/annie/reject", { proposal_id: card.proposal_id });
			} catch (error) {
				const { status, key } = extractAnnieError(error);
				trackError("annie_reject_error", { department_id: departmentId, proposal_id: card.proposal_id, status, message_key: key });
				// 404 = the proposal is already gone, so the dismissal stands. Anything else — a 409
				// "already confirmed" conflict or a transient failure — means no rejection was recorded,
				// so revert off the false "Dismissed" and surface why (mirrors approve()).
				if (status !== 404) {
					updateProposalItem(itemId, { status: "pending" });
					appendItem({ id: v4(), kind: "error", text: translateErrorKey(key) });
				}
			}
		},
		[isDraftMode, departmentId, appendItem, updateProposalItem, t, track, trackError, translateErrorKey],
	);

	return {
		items,
		statusText,
		workSteps,
		activeWorkStep,
		isStreaming,
		isLoadingSession,
		sessionId,
		sessions,
		isLoadingSessions,
		isSessionsError,
		deletingSessionId,
		send,
		chooseReadNurse,
		regenerate,
		choose,
		submitTimes,
		approve,
		reject,
		cancel,
		newSession,
		switchSession,
		deleteSession,
		saveFeedback,
	};
};
